import React, { Fragment } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import numberFormatter from "utils/numberFormatter";
import { Grid } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import Fade from "@material-ui/core/Fade";
import { BlueBox, PrimaryData, PrimaryDataCenter, Date } from "./CommonUtil";
import hedgeTypes from "constants/hedgeTypes";
import {
  SpreadMaintenanceResult,
  MakeWholeResult,
  YieldMaintenanceTraditionalResult,
  DefeasanceResult,
  YieldMaintenanceAgencyStyleResult,
  AggregateInterestResult,
  PaymentDateOnly
} from "./AnalysisDetailVariants";
import { BlueResult, FancyDivider, PrimaryDataWrapper, AddRow, AddIconWrapper, VerticalSpace, Disclosure, AdditionalInfoButton, AdditionalInformationWrapper, FlexContainer } from "./styles"



class PrepaymentAnalysisResult extends React.Component {

  renderPlusSign = (shouldRender, component) => {
    if (shouldRender) {
      return (<AddRow><AddIconWrapper><Add fontSize='inherit' /></AddIconWrapper>{component}</AddRow>);
    }

    return component;
  };

  renderPrimaryData = ({
    errorMessage,
    isOverview,
    isLoan,
    prepaymentType,
    prepaymentCost,
    prepaymentPenalty,
    accruedInterest,
    balanceAtPrepayment,
    totalDueToLender,
    defeasanceResult,
    isHedge,
    markToMarket,
    allInRate,
    pv01,
    notional,
    strike,
    terminationDate,
    midRate,
    hedgeTypeId,
    exitFee
  }) => {

    if (errorMessage) {
      const data = <p>
        { errorMessage }
        <br />
        Open <a href="/calculators/prepayment">stand-alone Prepayment calculator</a> to enter information.
      </p>;
      return <PrimaryDataCenter value={ data } label='' />;
    }

    const hasBalanceAtPrepayment = balanceAtPrepayment !== null;
    const hasPrepaymentPenalty = prepaymentPenalty !== null;
    const hasAccruedInterest = accruedInterest !== null;
    const hasDefeasanceResult = defeasanceResult !== null && defeasanceResult.totalThirdPartyFees !== null;
    const hasExitFee = exitFee !== null;

    if (isOverview || isLoan) {
      return (
        <PrimaryDataWrapper>
          <Fragment>
            {prepaymentType !== null && <PrimaryDataCenter value={prepaymentType.replace(/_/g, ' ')} label='' />}
            {prepaymentCost !== null && <BlueBox value={numberFormatter.formatMoney(prepaymentCost, 2)} label="Prepayment Cost" />}

            {hasBalanceAtPrepayment && <>{this.renderPlusSign(!hasPrepaymentPenalty && !hasAccruedInterest && !hasDefeasanceResult && !hasExitFee, <PrimaryData value={numberFormatter.formatMoney(balanceAtPrepayment, 2)} label='Balance at prepayment' />)}</>}
            {hasPrepaymentPenalty && <>{this.renderPlusSign(!hasAccruedInterest && !hasDefeasanceResult && !hasExitFee, <PrimaryData value={numberFormatter.formatMoney(prepaymentPenalty, 2)} label='Prepayment Penalty' />)}</>}
            {hasAccruedInterest && <>{this.renderPlusSign(!hasDefeasanceResult && !hasExitFee, <PrimaryData value={numberFormatter.formatMoney(accruedInterest, 2)} label='Accrued Interest' />)}</>}
            {hasDefeasanceResult && <>{this.renderPlusSign(!hasExitFee, <PrimaryData value={numberFormatter.formatMoney(defeasanceResult.totalThirdPartyFees, 2)} label='Transaction Costs' />)}</>}
            {hasExitFee && <>{this.renderPlusSign(hasExitFee, <PrimaryData value={numberFormatter.formatMoney(exitFee, 2)} label='Exit Fees' />)}</>}

            <FancyDivider />

            {totalDueToLender !== null && <PrimaryData value={numberFormatter.formatMoney(totalDueToLender, 2)} label='total due to lender' />}
          </Fragment>
        </PrimaryDataWrapper>
      );
    }

    if (isHedge) {
      return (
        <PrimaryDataWrapper>
          <Fragment>
            {markToMarket !== null && <BlueBox value={numberFormatter.formatMoney(markToMarket, 2)} label="Mark to Market" />}
            {hedgeTypeId !== null && hedgeTypeId === hedgeTypes.CAP && notional !== null && <PrimaryDataCenter value={numberFormatter.formatMoney(notional, 2)} label='Notional' />}
            {hedgeTypeId !== null && hedgeTypeId === hedgeTypes.CAP && strike !== null && <PrimaryDataCenter value={numberFormatter.formatRate(strike, 2)} label='Strike' />}
            {hedgeTypeId !== null && hedgeTypeId === hedgeTypes.CAP && terminationDate !== null && <Date value={moment(terminationDate).format('MM/DD/YYYY')} label='Termination Date' />}
            {hedgeTypeId !== null && hedgeTypeId === hedgeTypes.SWAP && allInRate !== null && <PrimaryDataCenter value={numberFormatter.formatRate(allInRate, 2)} label='All-In Rate' />}
            {hedgeTypeId !== null && hedgeTypeId === hedgeTypes.SWAP && pv01 !== null && <PrimaryDataCenter value={numberFormatter.formatMoney(pv01, 2)} label='PV01' />}
            {hedgeTypeId !== null && hedgeTypeId === hedgeTypes.SWAP && midRate !== null && <PrimaryDataCenter value={numberFormatter.formatRate(midRate, 2)} label='Mid Rate' />}
          </Fragment>
        </PrimaryDataWrapper>
      );
    }
  };

  render() {
    const { prepaymentResult, isAdditionalInfoOpen } = this.props;

    if (!prepaymentResult) {
      return null;
    }

    const {
      errorMessage,
      isLoan,
      makeWholeResult,
      yieldMaintenanceResult,
      yieldMaintenanceAgencyStyleResult,
      defeasanceResult,
      aggregateInterestResult,
      spreadMaintenanceResult,
      flatPercentageSchedule,
      prepaymentParameters
    } = prepaymentResult;

    return (
      <Fragment>
        <BlueResult>
          {!isAdditionalInfoOpen &&
            (<Fade in={!isAdditionalInfoOpen}>
              {this.renderPrimaryData(prepaymentResult)}
            </Fade >)}

          {isAdditionalInfoOpen &&
            (<Fade in={isAdditionalInfoOpen}>
              <AdditionalInformationWrapper>
                <FlexContainer>
                  <Grid container justify="space-between">
                    {makeWholeResult &&
                      <MakeWholeResult result={makeWholeResult} prepaymentResult={prepaymentResult} />
                    }
                    {yieldMaintenanceResult &&
                      <YieldMaintenanceTraditionalResult result={yieldMaintenanceResult} prepaymentResult={prepaymentResult} />
                    }
                    {yieldMaintenanceAgencyStyleResult &&
                      <YieldMaintenanceAgencyStyleResult result={yieldMaintenanceAgencyStyleResult} prepaymentResult={prepaymentResult} />
                    }
                    {defeasanceResult &&
                      <DefeasanceResult result={defeasanceResult} />
                    }
                    {aggregateInterestResult &&
                      <AggregateInterestResult result={aggregateInterestResult} />
                    }
                  {(spreadMaintenanceResult) &&
                    <SpreadMaintenanceResult result={spreadMaintenanceResult} prepaymentResult={prepaymentResult} prepaymentParameters={prepaymentParameters} />
                  }
                  {(flatPercentageSchedule) &&
                      <PaymentDateOnly result={prepaymentResult} />
                    }
                  </Grid>
                </FlexContainer>
              </AdditionalInformationWrapper>
            </Fade >)}

          {isLoan && (!errorMessage || errorMessage === "") && (<VerticalSpace>
            <AdditionalInfoButton onClick={this.props.toggleAdditionalInfo}>{this.props.additionalInfoButtonText}</AdditionalInfoButton>
          </VerticalSpace>)}
        </BlueResult>
        <Disclosure>Calculations are for informational purposes and subject to change with market conditions. These calculations are based on the next payment date.</Disclosure>
      </Fragment>
    )
  };
}

export default PrepaymentAnalysisResult;
